import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  LayoutDashboard,
  Settings,
  BarChart3,
  LogOut,
  Menu,
  X,
  Zap,
  MessagesSquare,
  Inbox,
  Archive,
  Headset,
  ChevronLeft,
  Star,
  MessageCircle,
  UserPlus,
  FileVideo,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  configAPI,
  usersAPI,
  ticketsAPI,
  internalChatAPI,
} from "../services/api";
import { useToast } from "@/hooks/use-toast";

const Layout = ({ children }) => {
  const { toast } = useToast();
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const canMonitorSupport = ["analyst", "admin"].includes(user?.role);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem("@up2-sidebar-collapsed");
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [systemConfig, setSystemConfig] = useState(null);

  const [showRating, setShowRating] = React.useState(false);
  const [userStats, setUserStats] = React.useState({ average: 0, count: 0 });

  const [badges, setBadges] = useState({
    queue: 0,
    potentials: 0,
    myChats: 0,
    internal: 0,
  });

  const toggleSidebarCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem("@up2-sidebar-collapsed", JSON.stringify(newState));
  };

  const fetchUserStats = async () => {
    try {
      const response = await usersAPI.getStats();
      setUserStats(response.data);
      setShowRating(!showRating);
    } catch (error) {
      console.error("Erro ao buscar notas:", error);
      toast({
        title: "Erro de Conexão",
        description: "Não foi possível carregar suas estatísticas.",
        variant: "destructive",
      });
    }
  };
useEffect(() => {
    const fetchNotifications = async () => {
      if (!user) return;
      try {
        const pendingRes = await ticketsAPI.getAll({
          status: "pending",
          limit: 500,
        });

        const allPending = pendingRes.data.items || [];

        const filaReal = allPending.filter(
          (t) => t.menu_response_received === true
        );

        const potenciais = allPending.filter(
          (t) => t.menu_response_received === false
        );

        let queueCount = 0;
        if (canMonitorSupport) {
          queueCount = filaReal.length;
        } else {
          const allowedTickets = filaReal.filter(
            (ticket) =>
              user.departments?.includes(ticket.category) ||
              user.departments?.includes(ticket.selected_menu_option),
          );
          queueCount = allowedTickets.length;
        }

        let potentialsCount = potenciais.length;

        const myTicketsRes = await ticketsAPI.getAll({
          status: "in_progress",
          assigned_to: user.id,
          limit: 100,
        });
        const unreadChatsCount =
          myTicketsRes.data.items?.reduce(
            (acc, ticket) => acc + (ticket.unread_count || 0),
            0,
          ) || 0;

        let internalCount = 0;
        try {
          const internalRes = await internalChatAPI.getUnreadCounts();
          if (internalRes.data && typeof internalRes.data === "object") {
            internalCount = Object.values(internalRes.data).reduce(
              (a, b) => a + b,
              0,
            );
          }
        } catch (e) {}

        setBadges({
          queue: queueCount,
          potentials: potentialsCount,
          myChats: unreadChatsCount,
          internal: internalCount,
        });
      } catch (error) {
        console.error("Erro ao buscar badges de notificação:", error);
      }
    };

    fetchNotifications();

    const interval = setInterval(fetchNotifications, 5000);
    return () => clearInterval(interval);
  }, [user, canMonitorSupport]);

  useEffect(() => {
    const isChatScreen =
      location.pathname.includes("/chat/") ||
      (location.pathname.startsWith("/tickets/") &&
        location.pathname !== "/tickets");

    if (isChatScreen) {
      setIsCollapsed(true);
      localStorage.setItem("@up2-sidebar-collapsed", JSON.stringify(true));
    }
  }, [location.pathname]);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await configAPI.get();
        setSystemConfig(response.data);

        if (response.data.logo_url) {
          let link = document.querySelector("link[rel~='icon']");

          if (!link) {
            link = document.createElement("link");
            link.rel = "icon";
            document.head.appendChild(link);
          }

          link.href = response.data.logo_url;
        }
      } catch (error) {
        console.error("Erro ao carregar configurações:", error);
      }
    };
    if (user) {
      fetchConfig();
    }
  }, [user]);

  const baseMenuItems = [
    { path: "/", icon: LayoutDashboard, label: "Dashboard" },
    {
      path: "/chat",
      icon: MessageCircle,
      label: "Central de Atendimentos",
      badgeKey: "myChats",
    },
    { path: "/queue", icon: Inbox, label: "Fila", badgeKey: "queue" },
    {
      path: "/potentials",
      icon: UserPlus,
      label: "Potenciais",
      badgeKey: "potentials",
    },
    { path: "/quick-replies", icon: Zap, label: "Respostas Rápidas" },
    { path: "/attendants", icon: Headset, label: "Equipe" },
    {
      path: "/internal-chat",
      icon: MessagesSquare,
      label: "Chat da Equipe",
      badgeKey: "internal",
    },
    { path: "/tickets", icon: Archive, label: "Histórico" },
    { path: "/procedures", icon: FileVideo, label: "Procedimentos" },
  ];

  const analysisMenuItems = [
    { path: "/reports", icon: BarChart3, label: "Relatórios" },
  ];
  const administrationMenuItems = [
    { path: "/config", icon: Settings, label: "Configurações" },
  ];

  const menuItems = [
    ...baseMenuItems,
    ...(canMonitorSupport ? analysisMenuItems : []),
    ...(user?.role === "admin" ? administrationMenuItems : []),
  ];

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const showLabels = sidebarOpen || !isCollapsed;

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <aside
        className={`fixed inset-y-0 left-0 z-50 bg-white border-r transform transition-all duration-300 ease-in-out flex flex-col
          lg:static lg:translate-x-0 
          ${sidebarOpen ? "translate-x-0 w-72" : "-translate-x-full lg:translate-x-0"}
          ${isCollapsed && !sidebarOpen ? "lg:w-20" : "lg:w-72"}
        `}
      >
        <div
          className={`flex items-center h-16 border-b transition-all duration-300 relative ${!showLabels ? "justify-center px-2" : "justify-between px-6"}`}
        >
          {showLabels && (
            <div className="flex items-center gap-2 overflow-hidden">
              {systemConfig?.logo_url && (
                <img
                  src={systemConfig.logo_url}
                  alt={systemConfig.company_name}
                  className="h-8 object-contain shrink-0"
                />
              )}
              <h1
                className="text-lg font-bold truncate"
                style={{ color: systemConfig?.primary_color || "#2563eb" }}
              >
                {systemConfig?.company_name || "UP2 - NossoSuporte"}
              </h1>
            </div>
          )}

          {!showLabels && systemConfig?.logo_url && (
            <img
              src={systemConfig.logo_url}
              alt="Logo"
              className="h-8 w-8 object-contain shrink-0"
            />
          )}

          <button
            className="lg:hidden shrink-0"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>

          <button
            onClick={toggleSidebarCollapse}
            className={`hidden lg:flex items-center justify-center absolute top-1/2 -translate-y-1/2 -right-3 w-6 h-6 bg-white border border-gray-200 shadow-sm rounded-full text-gray-500 hover:text-blue-600 hover:bg-gray-50 z-50 transition-transform ${isCollapsed ? "rotate-180" : ""}`}
            title={isCollapsed ? "Expandir menu" : "Recolher menu"}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-6 space-y-2 overflow-y-auto overflow-x-hidden custom-scrollbar">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            const badgeCount = item.badgeKey ? badges[item.badgeKey] : 0;

            return (
              <Link
                key={item.path}
                to={item.path}
                title={!showLabels ? item.label : ""}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`flex items-center rounded-lg transition-colors group relative
                  ${!showLabels ? "justify-center py-3 px-0" : "px-4 py-3"}
                  ${isActive ? "bg-blue-50 text-blue-600 font-medium" : "text-gray-700 hover:bg-gray-100"}
                `}
                onClick={() => setSidebarOpen(false)}
              >
                <div className="relative">
                  <Icon
                    className={`w-5 h-5 shrink-0 ${!showLabels ? "" : "mr-3"}`}
                  />

                  {!showLabels && badgeCount > 0 && (
                    <span
                      className={`absolute -top-1.5 -right-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-xs font-bold text-white shadow-sm ring-2 ring-white ${item.label === "Fila" ? "bg-red-500" : item.label === "Potenciais" ? "bg-orange-500" : "bg-blue-500"}`}
                    >
                      {badgeCount > 99 ? "99+" : badgeCount}
                    </span>
                  )}
                </div>

                {showLabels && (
                  <>
                    <span className="truncate whitespace-nowrap flex-1">
                      {item.label}
                    </span>

                    {badgeCount > 0 && (
                      <span
                        className={`ml-2 flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-xs font-bold text-white shadow-sm ${item.label === "Fila" ? "bg-red-500" : item.label === "Potenciais" ? "bg-orange-500" : "bg-blue-500"}`}
                      >
                        {badgeCount > 99 ? "99+" : badgeCount}
                      </span>
                    )}
                  </>
                )}
              </Link>
            );
          })}
        </nav>

        <div
          className={`p-4 border-t transition-all duration-300 flex relative ${!showLabels ? "flex-col items-center gap-4" : "flex-col gap-3"}`}
          style={{ overflow: "visible" }}
        >
          {showRating && (
            <div
              className={`absolute bottom-[85%] mb-2 bg-white border border-blue-100 shadow-2xl rounded-xl p-4 z-[100] animate-in fade-in slide-in-from-bottom-4
              ${!showLabels ? "left-12 w-48" : "left-4 right-4"}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-blue-500 uppercase tracking-widest">
                  Desempenho Geral
                </span>
                <X
                  className="w-3 h-3 cursor-pointer text-gray-400 hover:text-red-500 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowRating(false);
                  }}
                />
              </div>

              <div className="flex items-end gap-2 mb-3">
                <span className="text-3xl font-extrabold text-gray-900 leading-none">
                  {userStats.average}
                </span>
                <div className="flex flex-col">
                  <div className="flex text-yellow-400 mb-0.5">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-3 h-3 ${i < Math.round(userStats.average / 2) ? "fill-current" : "text-gray-200"}`}
                      />
                    ))}
                  </div>
                  <span className="text-xs text-gray-500 font-medium">
                    {userStats.count} avaliações recebidas
                  </span>
                </div>
              </div>

              <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden border border-gray-50">
                <div
                  className="bg-gradient-to-r from-blue-400 to-blue-600 h-full transition-all duration-1000 ease-out"
                  style={{ width: `${(userStats.average / 10) * 100}%` }}
                ></div>
              </div>
            </div>
          )}

          <div
            onClick={fetchUserStats}
            className={`flex items-center w-full cursor-pointer hover:bg-gray-50 p-2 rounded-xl transition-all ${!showLabels ? "justify-center" : ""}`}
          >
            <div
              className={`w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0 border-2 transition-all ${showRating ? "border-blue-500 ring-4 ring-blue-50" : "border-transparent"}`}
              title="Clique para ver sua média"
            >
              <span className="text-blue-600 font-bold text-lg">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>

            {showLabels && (
              <div className="ml-3 flex-1 overflow-hidden">
                <p className="text-sm font-bold text-gray-900 truncate">
                  {user?.name}
                </p>
                <div className="flex items-center gap-1.5">
                  <p className="text-[11px] text-gray-500 font-medium truncate">
                    {user?.role === "admin"
                      ? "Administrador"
                      : user?.role === "analyst"
                        ? "Analista"
                        : "Atendente"}
                  </p>
                  {userStats.count > 0 && (
                    <div className="flex items-center bg-yellow-50 px-1.5 py-0.5 rounded border border-yellow-200">
                      <Star className="w-2.5 h-2.5 text-yellow-500 fill-current mr-1" />
                      <span className="text-xs font-bold text-yellow-700">
                        {userStats.average}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {showLabels ? (
            <Button
              data-testid="logout-button"
              onClick={handleLogout}
              variant="outline"
              className="w-full justify-start"
            >
              <LogOut className="w-4 h-4 mr-2 shrink-0 text-red-500" />
              <span className="truncate text-red-600">Sair do Sistema</span>
            </Button>
          ) : (
            <button
              onClick={handleLogout}
              title="Sair do Sistema"
              className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut className="w-5 h-5" />
            </button>
          )}
        </div>
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden w-full relative">
        <header className="bg-white border-b h-16 flex items-center px-4 sm:px-6 shrink-0">
          <button
            className="lg:hidden mr-4 p-2 -ml-2 text-gray-600 hover:bg-gray-100 rounded-md"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-5 h-5" />
          </button>

          <h2 className="text-lg font-semibold text-gray-900 truncate">
            {menuItems.find((item) => item.path === location.pathname)?.label ||
              "Chat"}
          </h2>
        </header>

        <main className="flex-1 overflow-y-auto bg-gray-50 p-4 sm:p-6">
          {children}
        </main>
      </div>

      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm transition-opacity"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default Layout;
